import gsap from 'gsap';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';

// Components
import Backdrop from '../global/backdrop';

/**
 * ISI tray settings
 */
const settings = {
    containerId: 'isi-tray',

    selectors: {
        button: '.isi__bar button',
        scrollOuterContainer: '.isi__columns',
        scrollInnerContainer: '.isi__columns > div',
        collapsedContent: '.isi__collapsed-content',
        expandedContent: '.isi__expanded-content',
    },

    classNames: {
        visible: 'isi-tray--visible',
        collapsing: 'isi-tray--collapsing',
        collapsed: 'isi-tray--collapsed',
        open: 'isi-tray--open',
        closing: 'isi-tray--closing',
        scroll: 'isi-tray--scroll',
    },

    cssProperties: {
        scrollbarWidth: '--scrollbar-width',
    },

    animation: {
        slowMoScale: 4,
        openDuration: 0.5,
        closeDuration: 0.5,
    },
};

/**
 * Our single ISI tray instance, created via IsiTray.init()
 * @type {IsiTray}
 */
let isiTray = null;

/**
 * A floating ISI tray
 */
export default class IsiTray {
    /**
     * @constructor
     */
    constructor() {
        /** @type {boolean} */
        this.isVisible = false;

        /** @type {boolean} */
        this.isCollapsed = false;

        /** @type {boolean} */
        this.isOpen = false;

        /** @type {boolean} */
        this.scrollEnabled = false;

        // Locate the on-page ISI content and append ISI tray to the end of the DOM
        /** @type {Element} */
        this.onPageIsi = document.getElementById('isi');
        if (!this.onPageIsi) {
            return;
        }

        /** @type {Element} */
        this.container = document.createElement('section');
        this.container.id = settings.containerId;
        this.container.innerHTML = this.onPageIsi.innerHTML;
        this.container.setAttribute('aria-hidden', 'true');
        document.body.appendChild(this.container);

        // Locate remainaing DOM elements
        /** @type {Element} */
        this.scrollOuterContainer = this.container
            .querySelector(settings.selectors.scrollOuterContainer);

        /** @type {Element} */
        this.scrollInnerContainer = this.container
            .querySelector(settings.selectors.scrollInnerContainer);

        /** @type {Element} */
        this.collapsedContent = this.container
            .querySelector(settings.selectors.collapsedContent);

        /** @type {Element} */
        this.expandedContent = this.container
            .querySelector(settings.selectors.expandedContent);

        // Create backdrop
        /** @type {Backdrop} */
        this.backdrop = new Backdrop(
            this.container,
            (event) => {
                // Stop if we're not open
                if (!this.isOpen) {
                    return;
                }

                // Close the menu
                this.close(false, event.shiftKey);
            },
        );

        // Setup expand/collapse button
        /** @type {Element} */
        this.button = this.container.querySelector(settings.selectors.button);
        if (!this.button) {
            return;
        }

        this.button.addEventListener(
            'click',
            (event) => {
                event.preventDefault();
                event.stopPropagation();

                // Stop if we're not visible
                if (!this.isVisible) {
                    return;
                }

                // Toggle open/close state
                if (this.isOpen) {
                    this.close(false, event.shiftKey);
                } else {
                    this.open(false, event.shiftKey);
                }
            });

        // Setup window resize event
        window.addEventListener('resize', resizeCallback.bind(this));

        // An on-page ISI is present - setup scroll-based show/hide
        window.addEventListener('scroll', checkVisibilityCallback.bind(this));
        window.addEventListener('resize', checkVisibilityCallback.bind(this));
        checkVisibilityCallback.call(this);

        // Setup collapse on scroll
        if (!this.isCollapsed) {
            setTimeout(
                () => {
                    const collapseCallback = () => {
                        window.removeEventListener('scroll', collapseCallback);
                        this.collapse();
                    };

                    window.addEventListener('scroll', collapseCallback);
                },
                500,
            );
        }

        if (!this.isVisible) {
            // Check if a gravity form is present on the page; this can cause the
            // page height to change after it fully loads
            const observeElement = document.querySelector('.gform_wrapper');
            if (observeElement) {
                const observer = new MutationObserver(
                    (mutationList) => {
                        if (observeElement.clientHeight <= 0) {
                            return;
                        }

                        // Stop the observer
                        observer.disconnect();

                        // Re-check visibility
                        checkVisibilityCallback.call(this);
                    },
                );

                // Start watching for attribute changes; the "display: none;"
                // initially set on the form's HTML will be removed
                observer.observe(
                    observeElement,
                    {
                        attributes: true,
                        childList: false,
                        subtree: false,
                    },
                );
            }
        }
    }

    /**
     * Initializes the ISI tray if the associated element
     * is found on the DOM
     */
    static init() {
        // Stop if we've already initialized
        if (isiTray) {
            return;
        }

        isiTray = new IsiTray();
    }

    /**
     * Opens the ISI tray, if visible and closed
     * @param {boolean} immediate
     * @param {boolean} slowMo
     */
    open(
        immediate = false,
        slowMo = false,
    ) {
        if (this.isOpen) {
            return;
        }

        this.isOpen = true;

        // Stop any ongoing animation
        if (this.openCloseTimeline) {
            this.openCloseTimeline.progress(1);
        }

        // Lock scrolling
        disableBodyScroll(
            this.scrollOuterContainer,
            {
                reserveScrollBarGap: true,
            },
        );

        // Show our backdrop
        this.backdrop.show(immediate, slowMo);

        if (immediate) {
            // Add "open" class
            this.container
                .classList
                .add(settings.classNames.open);

            // Stop here
            return;
        }

        // Animate transition
        const initialHeight = this.scrollOuterContainer.clientHeight;

        let duration = settings.animation.openDuration;
        if (slowMo) {
            duration *= settings.animation.slowMoScale;
        }

        // Add "open" class
        this.container
            .classList
            .add(settings.classNames.open);

        // Determine if the container needs a scroll bar when opened
        if (
            this.scrollOuterContainer.clientHeight <
            this.scrollInnerContainer.clientHeight
        ) {
            // Enable scrolling
            enableScrolling.call(this, true);
        } else {
            // Disable scrolling
            enableScrolling.call(this, false);
        }

        this.openCloseTimeline = gsap.timeline();

        // Tray opens with a height transition
        this.openCloseTimeline.from(
            this.scrollOuterContainer,
            {
                duration: duration,
                ease: 'power2.out',
                height: initialHeight,
            },
            0,
        );

        // If collapsed and expanded content are present,
        // crossfade between them
        if (this.collapsedContent && this.expandedContent) {
            this.collapsedContent.style.position = 'absolute';
            this.collapsedContent.style.display = 'block';
            this.openCloseTimeline.to(
                this.collapsedContent,
                {
                    duration: duration,
                    ease: 'power3.out',
                    opacity: 0,
                },
                0,
            );

            this.expandedContent.style.position = 'absolute';
            this.openCloseTimeline.from(
                this.expandedContent,
                {
                    duration: duration,
                    ease: 'power3.out',
                    opacity: 0,
                },
                0,
            );
        }

        this.openCloseTimeline.add(() => {
            this.openCloseTimeline = null;
            this.scrollOuterContainer.removeAttribute('style');

            if (this.collapsedContent && this.expandedContent) {
                this.collapsedContent.removeAttribute('style');
                this.expandedContent.removeAttribute('style');
            }

            // Become collapsed once we've been opened
            if (!this.isCollapsed) {
                this.collapse(true);
            }
        });
    }

    /**
     * Closes the ISI tray, if visible and open
     * @param {boolean} immediate
     * @param {boolean} slowMo
     */
    close(
        immediate = false,
        slowMo = false,
    ) {
        if (!this.isOpen) {
            return;
        }

        this.isOpen = false;

        // Stop any ongoing animation
        if (this.openCloseTimeline) {
            this.openCloseTimeline.progress(1);
        }

        // Unlock scrolling
        enableBodyScroll(this.scrollOuterContainer);

        // Hide our backdrop
        this.backdrop.hide(immediate, slowMo);

        if (immediate) {
            // Remove "open" class
            this.container
                .classList
                .remove(settings.classNames.open);

            // Stop here
            return;
        }

        // Animate transition
        let duration = settings.animation.closeDuration;
        if (slowMo) {
            duration *= settings.animation.slowMoScale;
        }

        // Disable scrolling
        enableScrolling.call(this, false);

        // Remove "open" class
        this.container
            .classList
            .remove(settings.classNames.open);

        const finalHeight = this.scrollOuterContainer.clientHeight;

        // Add "closing" class
        this.container
            .classList
            .add(settings.classNames.closing);

        this.openCloseTimeline = gsap.timeline();

        // Tray closes with a height transition
        this.openCloseTimeline.to(
            this.scrollOuterContainer,
            {
                duration: duration,
                ease: 'power2.out',
                height: finalHeight,
            },
            0,
        );

        // If collapsed and expanded content are present,
        // crossfade between them
        if (this.collapsedContent && this.expandedContent) {
            this.collapsedContent.style.position = 'absolute';
            this.openCloseTimeline.from(
                this.collapsedContent,
                {
                    duration: duration,
                    ease: 'power3.out',
                    opacity: 0,
                },
                0,
            );

            this.expandedContent.style.position = 'absolute';
            this.expandedContent.style.display = 'block';
            this.openCloseTimeline.to(
                this.expandedContent,
                {
                    duration: duration,
                    ease: 'power3.out',
                    opacity: 0,
                },
                0,
            );
        }

        this.openCloseTimeline.add(() => {
            this.openCloseTimeline = null;
            this.scrollOuterContainer.removeAttribute('style');

            // Remove "closing" class
            this.container
                .classList
                .remove(settings.classNames.closing);

            if (this.collapsedContent && this.expandedContent) {
                this.collapsedContent.removeAttribute('style');
                this.expandedContent.removeAttribute('style');
            }
        });
    }

    /**
     * Closes the ISI tray, if visible and open
     * @param {boolean} immediate
     * @param {boolean} slowMo
     */
    collapse(
        immediate = false,
        slowMo = false,
    ) {
        if (this.isCollapsed) {
            return;
        }

        this.isCollapsed = true;

        // Stop any ongoing animation
        if (this.openCloseTimeline) {
            this.openCloseTimeline.progress(1);
        }

        if (immediate) {
            // Add "collapsed" class
            this.container
                .classList
                .add(settings.classNames.collapsed);

            // Stop here
            return;
        }

        // Animate transition
        const initialHeight = this.container.clientHeight;

        let duration = settings.animation.openDuration;
        if (slowMo) {
            duration *= settings.animation.slowMoScale;
        }

        // Add "collapsed" class
        this.container
            .classList
            .add(settings.classNames.collapsed);

        const finalHeight = this.container.clientHeight;

        // Add "collapsing" class
        this.container
            .classList
            .add(settings.classNames.collapsing);

        this.openCloseTimeline = gsap.timeline();

        // Tray collapses with a height transition
        this.openCloseTimeline.fromTo(
            this.container,
            {
                height: initialHeight,
            },
            {
                duration: duration,
                ease: 'power2.out',
                height: finalHeight,
            },
            0,
        );

        this.openCloseTimeline.add(() => {
            this.openCloseTimeline = null;
            this.container.removeAttribute('style');

            // Remove "collapsing" class
            this.container
                .classList
                .remove(settings.classNames.collapsing);
        });
    }
}

/*
    Internal functions
*/

/**
 * @this IsiTray
 */
function resizeCallback() {
    // Stop if we're not visible & open
    if (!this.isVisible || !this.isOpen) {
        return;
    }

    // Determine if the container needs a scroll bar
    if (
        this.scrollOuterContainer.clientHeight <
        this.scrollInnerContainer.clientHeight
    ) {
        // Enable scrolling
        enableScrolling.call(this, true);
    } else {
        // Disable scrolling
        enableScrolling.call(this, false);
    }
}

/**
 * @this IsiTray
 */
function checkVisibilityCallback() {
    // Stop if we're open
    if (this.isOpen) {
        return;
    }

    // Determine where we are on the page with respect to the on-page ISI
    const bounds = this.onPageIsi.getBoundingClientRect();
    const visibleOnPageIsiPixels = window.innerHeight - bounds.top;
    if (visibleOnPageIsiPixels >= this.container.clientHeight) {
        // The on-page ISI has scrolled onscreen and we shouldn't be visible
        if (this.isVisible) {
            this.isVisible = false;

            this.onPageIsi
                .classList
                .remove(settings.classNames.visible);

            this.container
                .classList
                .remove(settings.classNames.visible);
        }
    } else {
        // The on-page ISI hasn't scrolled onscreen and we should be visible
        if (!this.isVisible) {
            this.isVisible = true;

            this.onPageIsi
                .classList
                .add(settings.classNames.visible);

            this.container
                .classList
                .add(settings.classNames.visible);
        }
    }
}

/**
 * @this IsiTray
 * @param {boolean} enable
 */
function enableScrolling(enable) {
    if (enable) {
        if (!this.scrollEnabled) {
            this.scrollEnabled = true;
            this.container
                .classList
                .add(settings.classNames.scroll);

            // Set scrollbar width property
            this.container.style.setProperty(
                settings.cssProperties.scrollbarWidth,
                `${
                    this.scrollOuterContainer.offsetWidth -
                    this.scrollOuterContainer.clientWidth
                }px`,
            );
        }
    } else {
        if (this.scrollEnabled) {
            this.scrollEnabled = false;
            this.container
                .classList
                .remove(settings.classNames.scroll);

            // Remove scrollbar width property
            this.container.style.removeProperty(
                settings.cssProperties.scrollbarWidth,
            );

            // Scroll to top
            this.scrollOuterContainer.scrollTo({
                left: 0,
                top: 0,
                behavior: 'instant',
            });
        }
    }
}
